import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Eyebrow, Field, Input, Spinner, money } from '@busbook/ui';
import { useTenant } from '../lib/tenant.js';
import { useCustomer } from '../lib/customer-session.js';
import { api } from '../lib/api.js';
import { useLocale, useT } from '../i18n/index.js';

/**
 * Offers, and the code that earns them (DECISIONS D70).
 *
 * Three things in one place because they answer one question — "what am I owed?" — and
 * splitting them would make the referral programme a screen nobody finds. What the passenger
 * already holds comes first: it is the only part with a number attached to it.
 */

interface MyOffer {
  grantId: string;
  code: string;
  name: string;
  description: string | null;
  kind: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percentBps: number | null;
  amountCents: number | null;
  currency: string | null;
  usesRemaining: number;
  endsAt: string | null;
  expiresAt: string | null;
}

interface PublicOffer {
  code: string;
  name: string;
  description: string | null;
  kind: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percentBps: number | null;
  amountCents: number | null;
  currency: string | null;
  usesPerCustomer: number;
  endsAt: string | null;
}

interface ReferralSummary {
  code: string;
  pending: number;
  earned: number;
  joiningOffer: { name: string; description: string | null } | null;
  rewardOffer: { name: string; description: string | null } | null;
}

/** "5% off" or "€10 off" — the offer in the shape a passenger reads it. */
function offerValue(
  offer: Pick<MyOffer, 'kind' | 'percentBps' | 'amountCents' | 'currency'>,
): string {
  if (offer.kind === 'PERCENTAGE' && offer.percentBps !== null) {
    // Basis points are an implementation detail; nobody says "500 basis points off".
    const percent = offer.percentBps / 100;
    return offer.percentBps >= 10_000 ? 'Free' : `${Number(percent.toFixed(2))}% off`;
  }
  if (offer.amountCents !== null && offer.currency) {
    return `${money(offer.amountCents, offer.currency)} off`;
  }
  return '';
}

export function Offers() {
  const t = useT();
  const { locale } = useLocale();
  const { branding } = useTenant();
  const { user, loading: sessionLoading } = useCustomer();

  const [mine, setMine] = useState<MyOffer[] | null>(null);
  const [available, setAvailable] = useState<PublicOffer[] | null>(null);
  const [referral, setReferral] = useState<ReferralSummary | null>(null);

  const load = useCallback(async () => {
    // Public offers are visible to anybody; the rest needs an account.
    const offers = api.request<PublicOffer[]>('/public/promotions/offers', {
      method: 'GET',
      auth: false,
    });
    if (!user) {
      setAvailable(await offers.catch(() => []));
      setMine([]);
      setReferral(null);
      return;
    }
    const [pub, held, ref] = await Promise.all([
      offers.catch(() => []),
      api.get<MyOffer[]>('/public/promotions/mine').catch(() => []),
      api.get<ReferralSummary>('/public/referrals/me').catch(() => null),
    ]);
    setAvailable(pub);
    setMine(held);
    setReferral(ref);
  }, [user]);

  useEffect(() => {
    if (sessionLoading) return;
    void load();
  }, [sessionLoading, load]);

  const loading = sessionLoading || available === null;

  return (
    <section className="px-4 py-6">
      <Eyebrow>{t('o.title')}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{branding.appName}</h1>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Spinner label={t('m.loading')} />
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-6">
          {user && mine && mine.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold">{t('o.yours')}</h2>
              <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                {t('o.yoursHint')}
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {mine.map((offer) => (
                  <li key={offer.grantId}>
                    <OfferCard
                      title={offer.name}
                      value={offerValue(offer)}
                      description={offer.description}
                      code={offer.code}
                      footnote={
                        offer.usesRemaining === 1
                          ? t('o.rideLeft')
                          : t('o.ridesLeft', { count: offer.usesRemaining })
                      }
                      held
                    />
                    {offer.expiresAt && (
                      <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                        {t('o.expires', {
                          date: new Date(offer.expiresAt).toLocaleDateString(locale, {
                            day: 'numeric',
                            month: 'short',
                          }),
                        })}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {user && <ClaimCode onClaimed={() => void load()} />}

          <div>
            <h2 className="text-sm font-semibold">{t('o.available')}</h2>
            <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
              {t('o.availableHint')}
            </p>

            {available.length === 0 ? (
              <div className="mt-3">
                <EmptyState title={t('o.none')} description={t('o.noneHint')} />
              </div>
            ) : (
              <ul className="mt-3 flex flex-col gap-2">
                {available.map((offer) => (
                  <li key={offer.code}>
                    <OfferCard
                      title={offer.name}
                      value={offerValue(offer)}
                      description={offer.description}
                      code={offer.code}
                      footnote={
                        offer.usesPerCustomer > 1
                          ? t('o.ridesLeft', { count: offer.usesPerCustomer })
                          : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {referral && <ReferralCard summary={referral} />}

          {!user && (
            <Card>
              <p className="text-sm">{t('m.signInToSeeHint')}</p>
              <Link
                to="/auth/login?next=%2Foffers"
                className="mt-3 inline-block text-sm font-medium underline underline-offset-4"
                style={{ color: 'var(--color-primary)' }}
              >
                {t('c.signIn')}
              </Link>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

function OfferCard({
  title,
  value,
  description,
  code,
  footnote,
  held = false,
}: {
  title: string;
  value: string;
  description: string | null;
  code: string;
  footnote?: string;
  held?: boolean;
}) {
  return (
    <div
      className="rounded-xl border p-4"
      style={{
        borderColor: held ? 'var(--color-primary)' : 'var(--color-line)',
        background: 'var(--color-surface)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          {description && (
            <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
              {description}
            </p>
          )}
        </div>
        {value && (
          <span
            className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
            style={{ background: 'var(--color-primary-wash)', color: 'var(--color-primary)' }}
          >
            {value}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <code
          className="tabular rounded-md border px-2 py-1 text-xs font-semibold tracking-wider"
          style={{ borderColor: 'var(--color-line)' }}
        >
          {code}
        </code>
        {footnote && (
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {footnote}
          </span>
        )}
      </div>
    </div>
  );
}

function ClaimCode({ onClaimed }: { onClaimed: () => void }) {
  const t = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await api.post('/public/promotions/redeem', { code: code.trim() });
      setMessage({ tone: 'success', text: t('o.claimed') });
      setCode('');
      onClaimed();
    } catch {
      setMessage({ tone: 'error', text: t('o.claimFailed') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Eyebrow>{t('o.claim')}</Eyebrow>
      <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
        <Field label={t('o.claim')} htmlFor="claim-code">
          <Input
            id="claim-code"
            className="tabular uppercase"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
          />
        </Field>
        {message && <Alert tone={message.tone}>{message.text}</Alert>}
        <Button type="submit" variant="secondary" block loading={busy}>
          {t('o.claimAction')}
        </Button>
      </form>
    </Card>
  );
}

function ReferralCard({ summary }: { summary: ReferralSummary }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(summary.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the code is on screen either way, which is why
      // this fails quietly rather than reporting something the passenger cannot act on.
    }
  }

  return (
    <Card>
      <Eyebrow>{t('o.refer')}</Eyebrow>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {summary.joiningOffer?.description ?? t('o.referHint')}
      </p>

      <div
        className="mt-3 flex items-center justify-between gap-3 rounded-lg border p-3"
        style={{ borderColor: 'var(--color-line)' }}
      >
        <div className="min-w-0">
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('o.yourCode')}
          </p>
          <code className="tabular text-lg font-bold tracking-[0.2em]">{summary.code}</code>
        </div>
        <Button size="sm" variant="secondary" onClick={copy}>
          {copied ? t('o.copied') : t('o.copy')}
        </Button>
      </div>

      <dl className="mt-3 flex gap-6 text-sm">
        <div>
          <dt className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('o.waiting', { count: summary.pending })}
          </dt>
        </div>
        <div>
          <dt className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('o.earned', { count: summary.earned })}
          </dt>
        </div>
      </dl>
    </Card>
  );
}
